import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CalendarDays, ChevronDown, Clock, MapPin, Smartphone, Ticket, UserCircle } from "lucide-react";
import {
  buildActivityFromState,
  defaultActivity,
  formatActivityCurrency,
  formatActivityDate,
  getTotalAmount,
  packageOptions,
} from "./Confirm.js";
import "./Confirm.css";

export default function ActivityConfirm() {
  const location = useLocation();
  const navigate = useNavigate();
  const activity = useMemo(() => buildActivityFromState(location.state?.activity), [location.state]);
  const [selectedImage, setSelectedImage] = useState(activity.gallery?.[0] || activity.image || defaultActivity.image);
  const [selectedPackage, setSelectedPackage] = useState(packageOptions[0]);
  const [date, setDate] = useState(activity.date || defaultActivity.date);
  const [time, setTime] = useState(activity.time || defaultActivity.time);
  const [travelers, setTravelers] = useState(activity.travelers || defaultActivity.travelers);
  const totalAmount = getTotalAmount(selectedPackage, travelers);
  const openPaymentPage = () => {
    navigate("/activities/payment", {
      state: {
        activity,
        packageName: selectedPackage.title,
        amount: totalAmount,
        date,
        time,
        travelers,
      },
    });
  };

  return (
    <main className="activity-confirm-page">
      <section className="activity-confirm-shell">
        <div className="activity-confirm-header">
          <h1>{activity.title}</h1>
          <div className="activity-confirm-meta">
            <span className="activity-stars">★★★★★</span>
            <span>{activity.reviews} Reviews</span>
            <span><MapPin size={18} /> {activity.location}</span>
          </div>
        </div>

        <div className="activity-confirm-layout">
          <section className="activity-confirm-main">
            <div className="activity-gallery">
              <div className="activity-thumbs">
                {activity.gallery.map((image, index) => (
                  <button
                    type="button"
                    key={`${image}-${index}`}
                    className={selectedImage === image ? "is-active" : ""}
                    onClick={() => setSelectedImage(image)}
                  >
                    <img src={image} alt={`${activity.title} ${index + 1}`} />
                    {index === 3 && <span>See More</span>}
                  </button>
                ))}
              </div>
              <div className="activity-main-image">
                <img src={selectedImage} alt={activity.title} />
                <button type="button" aria-label="Next image">›</button>
              </div>
            </div>

            <div className="activity-feature-row">
              <span><Clock size={22} /> 1 hour (approx.)</span>
              <span><Smartphone size={22} /> Mobile Ticket</span>
              <span><Ticket size={22} /> Offered In: English</span>
            </div>

            <div className="activity-tabs">
              <button type="button" className="is-active">Package Options</button>
              <button type="button">Overview</button>
              <button type="button">Address & Map</button>
            </div>

            <section className="activity-options-card">
              <h2>Select Package Options</h2>
              {packageOptions.map((option) => (
                <button
                  type="button"
                  key={option.id}
                  className={`activity-option ${selectedPackage.id === option.id ? "is-selected" : ""}`}
                  onClick={() => setSelectedPackage(option)}
                >
                  <div className="activity-option-copy">
                    <h3>{option.title}</h3>
                    <span><Clock size={15} /> 1 hour (approx.)</span>
                    <ul>{option.details.map((detail) => <li key={detail}>{detail}</li>)}</ul>
                  </div>
                  <strong>{formatActivityCurrency(option.price)}</strong>
                  {selectedPackage.id === option.id && <em>✓</em>}
                </button>
              ))}
            </section>
          </section>

          <aside className="activity-confirm-side">
            <section className="activity-book-card">
              <p>Starting from <strong>{formatActivityCurrency(selectedPackage.price)}</strong> per Person <small>ⓘ</small></p>
              <h2>Select Date, Time and Travelers</h2>
              <label><CalendarDays size={20} /><input type="date" value={date} onChange={(event) => setDate(event.target.value)} /></label>
              <label><Clock size={20} /><select value={time} onChange={(event) => setTime(event.target.value)}><option>06:30 PM</option><option>07:45 PM</option></select><ChevronDown size={18} /></label>
              <label><UserCircle size={22} /><select value={travelers} onChange={(event) => setTravelers(Number(event.target.value))}>{[1, 2, 3, 4, 5].map((count) => <option key={count} value={count}>{count} {count === 1 ? "adult" : "adults"}</option>)}</select></label>
              <div className="activity-total-row"><span>Total Amount to Pay</span><strong>{formatActivityCurrency(totalAmount)}</strong></div>
              <button type="button" className="activity-book-now" onClick={openPaymentPage}>Book Now</button>
            </section>

            <section className="activity-policy-card">
              <h3>● CANCELLATION POLICY</h3>
              <p>Cancellation needs to be done at least 3 days before the activity.</p>
            </section>

            <section className="activity-info-card">
              <h3>Important Information</h3>
              <ul>
                <li>The minimum age limit for the event is 2 years.</li>
                <li>Please reach the venue 1 hour before the show timings.</li>
                <li>Entry starts at 6:00 P.M for the first show.</li>
                <li>Selected date: {formatActivityDate(date)}.</li>
              </ul>
            </section>
          </aside>
        </div>
      </section>
    </main>
  );
}
